import React, { useEffect, useState } from 'react';
import offerApi from '../api/offer';
import { IOffer, IOfferFilter } from '../interface/IOffer';
import { useToast } from '../helper/ToastProvider';
import { useNavigate } from 'react-router-dom';
import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded';
import Breadcrumb from '../components/Breadcrumb';
import { Box, Button, Sheet, Stack, Typography } from '@mui/joy';
import OfferTableFilter from '../components/OfferTableFilter';
import { styled } from '@mui/joy/styles';
import OfferTable from '../components/OfferTable';
import OrderedOfferModal from '../components/OrderedOfferModal';
import { deutschNumberFormatter } from '../helper/utils';

type Props = {};

const Item = styled(Sheet)(({ theme }) => ({
  ...theme.typography['body-sm'],
  textAlign: 'center',
  fontWeight: theme.fontWeight.md,
  color: theme.vars.palette.text.secondary,
  border: '1px solid',
  borderColor: theme.palette.divider,
  padding: theme.spacing(1),
  borderRadius: theme.radius.md,
  flex: 0,
  whiteSpace: 'nowrap',
}));

const Offers = (props: Props) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [offers, setOffers] = useState<IOffer[]>();
  const [totalOffer, setTotalOffer] = useState<number>(0);
  const [totalOfferValue, setTotalOfferValue] = useState<number>(0);
  const [selectedOffer, setSelectedOffer] = useState<IOffer | null>();
  const [openOfferedModal, setOpenOfferedModal] = useState(false);

  const [filters, setFilters] = useState<IOfferFilter>({
    search: null,
    status: null,
    createDate: undefined,
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sortBy, setSortBy] = useState<keyof IOffer | undefined>();
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

  useEffect(() => {
    getOffersPaginated(page, rowsPerPage, { ...filters, sortedBy: sortBy, sortedOrder: sortOrder });
  }, [page, rowsPerPage, sortBy, sortOrder]);

  const onFilterChange = (filter: keyof IOfferFilter, value: any) => {
    const updatedFilters = {
      ...filters,
      [filter]: value === '' || value === 'all' ? null : value,
    };
    const activeFilters = Object.fromEntries(Object.entries(updatedFilters).filter(([_, v]) => v !== null && v !== undefined && v !== ''));
    setFilters(activeFilters);
  };

  const onFilterApply = () => {
    setPage(0);
    getOffersPaginated(0, rowsPerPage, { ...filters, sortedBy: sortBy, sortedOrder: sortOrder });
  };

  const onFilterReset = () => {
    setFilters({});
    setPage(0);
    getOffersPaginated(0, rowsPerPage, { sortedBy: sortBy, sortedOrder: sortOrder });
  };

  const getOffersPaginated = async (_page: number, _limit: number, _filters: IOfferFilter) => {
    try {
      setLoading(true);
      const res = await offerApi.getOffersPaginated(_page, _limit, _filters);
      setOffers(res.data.data);
      setTotalOffer(res.data.total);
      setTotalOfferValue(res.data.totalOfferValue);
      setLoading(false);
    } catch (e) {
      showToast({
        message: 'Offer Fetch Failed',
        severity: 'danger',
      });
      setLoading(false);
    }
  };

  const handleSort = (column: keyof IOffer) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
  };

  const closeOrderedOfferModal = () => {
    setOpenOfferedModal(false);
    setSelectedOffer(null);
  };

  return (
    <div>
      <Breadcrumb crumb={['Offers']} />
      <Box
        sx={{
          display: 'flex',
          mb: 1,
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'start', sm: 'center' },
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <Typography level='h2' component='h1'>
          Offers
        </Typography>
        <Button color='primary' onClick={() => navigate('create')} startDecorator={<PersonAddRoundedIcon />} size='sm'>
          Create New
        </Button>
      </Box>
      {offers && (
        <>
          <Box sx={{ width: '100%' }}>
            <Stack spacing={2} direction={'row'} sx={{ alignItems: 'flex-start', marginBottom: 2 }}>
              <OfferTableFilter filters={filters} onFilterChange={onFilterChange} onFilterApply={onFilterApply} onFilterReset={onFilterReset} />
            </Stack>
            <Stack spacing={2} direction={'row'} sx={{ alignItems: 'flex-start', marginBottom: 2 }}>
              <Item>Offers: {totalOffer}</Item>
              <Item>Total Offer Value: {deutschNumberFormatter(totalOfferValue)} €</Item>
              <Item>Total Open Budget: wip €</Item>
            </Stack>
          </Box>
          <OfferTable
            loading={loading}
            order={sortOrder}
            setOrder={setSortOrder}
            orderBy={sortBy}
            setOrderBy={setSortBy}
            setPage={setPage}
            page={page}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            offers={offers}
            totalOffer={totalOffer}
            onEdit={offerId => navigate(`create?offer=${offerId}`)}
            onDuplicate={offerId => navigate(`create?duplicate=${offerId}`)}
            onDelete={async offerId => {
              await offerApi.deleteOffer(offerId);
              await getOffersPaginated(page, rowsPerPage, { ...filters, sortedBy: sortBy, sortedOrder: sortOrder });
            }}
            onOrdered={(offer: IOffer) => {
              setSelectedOffer(offer);
              setOpenOfferedModal(true);
            }}
            onStatusChange={async (offerId: string, newStatus: string) => {
              await offerApi.changeStatusOffer(offerId, { status: newStatus });
              await getOffersPaginated(page, rowsPerPage, { ...filters, sortedBy: sortBy, sortedOrder: sortOrder });
            }}
          />
        </>
      )}
      <OrderedOfferModal
        open={openOfferedModal}
        onClose={closeOrderedOfferModal}
        onSave={async () => {
          try {
            await getOffersPaginated(page, rowsPerPage, { ...filters, sortedBy: sortBy, sortedOrder: sortOrder });
            closeOrderedOfferModal();
            showToast({
              message: 'Offer Order Success',
              severity: 'success',
            });
          } catch (e) {
            showToast({
              message: 'Offer Order Failed',
              severity: 'danger',
            });
          }
        }}
        selectedOffer={selectedOffer!}
      />
    </div>
  );
};

export default Offers;
